import { Category, FilterBy, Flatten, makeDefinition, SecondLayerKeys } from "./Category"
import { Enum } from "./Enum"
import { IndustryTypes } from "./IndustryTypes"
import { PriceFieldCategories } from "./PriceFieldCategories"
import { EnumPresentMap, SearchableEnumProperties } from "./PropertyMap"

export type RoomDescription<R extends Enum, C extends Enum> = Category<R> & {
    categories: SearchableEnumProperties<C, boolean>
}
export namespace Rooms {
    // Will enforce the shape while allowing keys and values to slip through
    function enforceType<T extends {
        [P in IndustryTypes.IndustryType]: {
            [key: string]: Omit<RoomDescription<T[P], PriceFieldCategories.TypesFor<P>>, 'id'>
        }
    }>(val: T) {
        return val
    }

    export type Cat = Category<typeof FlatEnum>

    const commonElectricalCategories: EnumPresentMap<PriceFieldCategories.TypesFor<typeof IndustryTypes.FlatEnum.ELECTRICAL>> = {
        ELECTRICAL_CABLES: true,
        ELECTRICAL_SOCKETS: true,
        ELECTRICAL_LIGHTING: true,
        ELECTRICAL_SWITCHES: true,
        ELECTRICAL_HEATING: true,
        ELECTRICAL_SMARTHOME: true,
        ELECTRICAL_FASTENERS: true,
        ELECTRICAL_DOCUMENTATION: true,
        ELECTRICAL_WORK_TIME: true,
    }

    export const { Flat, FlatList, FlatEnum, FlatEnumList, Grouped, GroupedList, GroupedEnum, GroupedEnumList } = makeDefinition(enforceType({
        [IndustryTypes.FlatEnum.ELECTRICAL]: {
            BEDROOM: {
                name: 'Soverom',
                description: 'Soverom',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                    },
                },
            },
            LIVING_ROOM: {
                name: 'Stue',
                description: 'Stue',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                    },
                },
            },
            BATHROOM: {
                name: 'Bad',
                description: 'Bad',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                        [PriceFieldCategories.FlatEnum.ELECTRICAL_WET_ROOM]: true,
                    },
                },
            },
            GARAGE: {
                name: 'Garasje',
                description: 'Garasje',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                        [PriceFieldCategories.FlatEnum.ELECTRICAL_OUTSIDE]: true,
                        [PriceFieldCategories.FlatEnum.ELECTRICAL_SUPPLY]: true,
                    },
                },
            },
            HALLWAY: {
                name: 'Gang',
                description: 'Gang',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                    },
                },
            },
            FUSE_BOX: {
                name: 'Sikringsboks',
                description: 'Sikringsboks',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                        [PriceFieldCategories.FlatEnum.ELECTRICAL_SUPPLY]: true,
                    },
                },
            },
            KITCHEN: {
                name: 'Kjøkken',
                description: 'Kjøkken',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                        [PriceFieldCategories.FlatEnum.ELECTRICAL_APPLIANCES]: true,
                    },
                },
            },
            OTHER: {
                name: 'Annet',
                description: 'Andre rom',
                categories: {
                    map: {
                        ...commonElectricalCategories,
                    },
                },
            },
        },
        [IndustryTypes.FlatEnum.CARPENTRY]: {
            BEDROOM: {
                name: 'Soverom',
                description: 'Soverom',
                categories: {
                    map: {
                    },
                },
            },
        },
        [IndustryTypes.FlatEnum.GARDENING]: {
            BEDROOM: {
                name: 'Soverom',
                description: 'Soverom',
                categories: {
                    map: {
                    },
                },
            },
        },
        [IndustryTypes.FlatEnum.PLUMBING]: {
            LAUNDRY_ROOM: {
                name: 'Vaskerom',
                description: 'Vaskerom',
                categories: {
                    map: {
                    },
                },
            },
            KITCHEN: {
                name: 'Kjøkken',
                description: 'Kjøkken',
                categories: {
                    map: {
                    },
                },
            },
            BATHROOM: {
                name: 'Bad',
                description: 'Bad',
                categories: {
                    map: {
                    },
                },
            },
            WC: {
                name: 'WC',
                description: 'Toalett rom',
                categories: {
                    map: {
                    },
                },
            },
            OUTSIE: {
                name: 'Utvendig',
                description: 'Utenfor huset',
                categories: {
                    map: {
                    },
                },
            },
            TECHNICAL_ROOM: {
                name: 'Teknisk rom',
                description: 'Teknisk rom',
                categories: {
                    map: {
                    },
                },
            },
            OTHER: {
                name: 'Annet',
                description: 'Andre rom',
                categories: {
                    map: {
                    },
                },
            },
            BEDROOM: {
                name: 'Soverom',
                description: 'Soverom',
                categories: {
                    map: {
                    },
                },
            },
        },
    }))


    export type TypesFor<T extends IndustryTypes.IndustryType> = SecondLayerKeys<FilterBy<typeof GroupedEnum, T>>
    export type TypeFor<T extends IndustryTypes.IndustryType> = Flatten<typeof Grouped, T>[keyof Flatten<typeof Grouped, T>]
    export type Types = typeof FlatEnum
    export type Type = keyof typeof FlatEnum
}
